/**
 * The block catalogue the editor offers, and how to change a block list.
 *
 * <b>This mirrors CagistaServices' SiteHomeValidator and is not the authority.</b> The server
 * whitelists, clamps and re-orders everything on save regardless of what is sent — this exists so the
 * editor offers the right things and does not let a merchant build something that will silently come
 * back different. When the two disagree, the server is right and this is the bug.
 */

/** Added back by the server if missing, so the editor must not offer to remove them. */
export const REQUIRED_TYPES = ['hero', 'menu', 'footer'];

/** Everything else may appear once only — a second copy just repeats generated content further down. */
export const REPEATABLE_TYPES = ['usp', 'about', 'gallery', 'reviews', 'cta'];

export const BLOCK_CATALOGUE = [
    { type: 'hero',      label: 'Toppbilde',      hint: 'Navn, én setning og et stort bilde' },
    { type: 'usp',       label: 'Det vi tilbyr',  hint: 'Tjenestene du tilbyr, og det du vil skryte av' },
    { type: 'menu',      label: 'Meny',           hint: 'Hentes fra Produkter — du skriver den ikke her' },
    { type: 'about',     label: 'Om oss',         hint: 'Om restauranten og hva den er kjent for' },
    { type: 'gallery',   label: 'Galleri',        hint: 'Bilder av maten og lokalet' },
    { type: 'hours',     label: 'Åpningstider',   hint: 'Hentes fra Åpningstider — alltid riktige' },
    { type: 'contact',   label: 'Kontakt',        hint: 'Adresse, telefon og kart fra Avdelinger' },
    { type: 'locations', label: 'Våre avdelinger',    hint: 'For deg med flere avdelinger' },
    { type: 'reviews',   label: 'Omtaler',        hint: 'Sitater fra gjester' },
    { type: 'cta',       label: 'Bestill-knapp',   hint: 'En siste oppfordring til å bestille, nederst på nettsiden' },
    { type: 'footer',    label: 'Bunn',           hint: 'Sosiale lenker' }
];

/**
 * The services a «Det vi tilbyr» item can be, mirroring CagistaServices' SiteHomeServices.
 *
 * A service item is shown on the page only while that service is switched on, so a restaurant that does not
 * deliver never promises delivery. `needs` is what the merchant has to switch on, in their words.
 */
export const USP_SERVICES = [
    { key: 'pickup',   label: 'Henting',            needs: 'nettbestilling', icon: 'bag',      title: 'Henting',            body: 'Bestill på nett og hent selv.' },
    { key: 'delivery', label: 'Levering',           needs: 'levering',       icon: 'truck',    title: 'Levering',           body: 'Hjem til døren.' },
    { key: 'qr',       label: 'Bestill fra bordet', needs: 'QR-bestilling',  icon: 'qr',       title: 'Bestill fra bordet', body: 'Skann koden på bordet og bestill.' },
    { key: 'booking',  label: 'Bordbestilling',     needs: 'bordbestilling', icon: 'calendar', title: 'Bordbestilling',     body: 'Book bord på nett.' }
];

/** The icons an item may carry — the set SiteHomeValidator allows, in the words the panel shows. */
export const USP_ICONS = [
    { key: 'bag', label: 'Pose' },
    { key: 'truck', label: 'Bil' },
    { key: 'qr', label: 'QR-kode' },
    { key: 'calendar', label: 'Kalender' },
    { key: 'clock', label: 'Klokke' },
    { key: 'leaf', label: 'Blad' },
    { key: 'utensils', label: 'Bestikk' },
    { key: 'fire', label: 'Flamme' },
    { key: 'star', label: 'Stjerne' },
    { key: 'heart', label: 'Hjerte' },
    { key: 'award', label: 'Medalje' },
    { key: 'phone', label: 'Telefon' },
    { key: 'card', label: 'Kort' }
];

/** As many as SiteHomeValidator keeps; a seventh would vanish on save. */
export const MAX_USP_ITEMS = 6;

/** A new item: a service with its own words and icon, or an empty promise («» as service) to write. */
export function newUspItem(serviceKey){
    const service = USP_SERVICES.find(s => s.key === serviceKey);
    return service
        ? { service: service.key, icon: service.icon, title: service.title, body: service.body }
        : { service: '', icon: 'star', title: 'Nytt løfte', body: '' };
}

const NEW_BLOCK = {
    usp: () => ({ items: [newUspItem('pickup'), newUspItem('delivery'),
        { service: '', icon: 'leaf', title: 'Ferske råvarer', body: 'Laget samme dag.' }] }),
    about: () => ({ heading: 'Om oss', body: '' }),
    gallery: () => ({ heading: 'Galleri', images: [] }),
    hours: () => ({ heading: 'Åpningstider' }),
    contact: () => ({ heading: 'Kontakt', showMap: true }),
    locations: () => ({ heading: 'Våre avdelinger' }),
    reviews: () => ({ heading: 'Omtaler', items: [{ quote: '', name: '' }] }),
    cta: () => ({ headline: 'Sulten?', body: '' }),
    menu: () => ({ heading: 'Meny', style: 'cards', categoryIds: [] }),
    hero: () => ({ headline: '', subheadline: '', image: '', badges: [] }),
    footer: () => ({ social: {} })
};

/**
 * Where a newly added section goes.
 *
 * «Det vi tilbyr» right under the hero — below any already there — because that is where it does its job:
 * what the restaurant offers, before the menu. The seeded page and both layouts put it there too, and a
 * merchant who added it back found it at the bottom of the page (founder, 2026-10-31). They can move it after.
 * Everything else goes last, before the footer, which the server pins to the bottom anyway.
 */
export function insertIndex(blocks, type){
    const list = blocks || [];
    if (type === 'usp') {
        // No hero gives 0; the server puts the hero back first, so the section still ends up under it.
        let at = list.findIndex(b => b.type === 'hero') + 1;
        while (at < list.length && list[at].type === 'usp') at++;
        return at;
    }
    const footerAt = list.findIndex(b => b.type === 'footer');
    return footerAt < 0 ? list.length : footerAt;
}

export function blockLabel(type){
    const entry = BLOCK_CATALOGUE.find(b => b.type === type);
    return entry ? entry.label : type;
}

export function isRequired(type){
    return REQUIRED_TYPES.indexOf(type) >= 0;
}

/** Which types can still be added, given what is on the page. */
export function addableTypes(blocks){
    const present = (blocks || []).map(b => b.type);
    return BLOCK_CATALOGUE.filter(entry =>
        REPEATABLE_TYPES.indexOf(entry.type) >= 0 || present.indexOf(entry.type) < 0);
}

/** A new block with a unique, URL-safe id — it is also the nav anchor, so it must not collide. */
export function makeBlock(type, blocks){
    const taken = (blocks || []).map(b => b.id);
    let id = type;
    let n = 2;
    while (taken.indexOf(id) >= 0) {
        id = type + '-' + (n++);
    }
    const factory = NEW_BLOCK[type] || (() => ({}));
    return Object.assign({ type: type, id: id }, factory());
}

/**
 * Write a value at a dotted path: `headline`, or `items.0.title`.
 *
 * The editor patches by path because a block's repeated parts — a USP promise, a review — are edited
 * in place on the page, and the message that comes back has to say WHICH one.
 */
export function setPath(target, path, value){
    const parts = String(path).split('.');
    let node = target;
    for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (node[key] === undefined || node[key] === null) {
            node[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
        }
        node = node[key];
    }
    node[parts[parts.length - 1]] = value;
    return target;
}

/**
 * Move a block, keeping the hero first and the footer last.
 *
 * The server enforces that anyway, so the only thing this prevents is the editor showing a move that
 * silently undoes itself on save — which reads as the page being broken rather than as a rule.
 */
export function moveBlock(blocks, id, direction){
    const i = blocks.findIndex(b => b.id === id);
    if (i < 0) return blocks;
    const target = i + direction;
    if (target < 0 || target >= blocks.length) return blocks;

    const pinned = t => t === 'hero' || t === 'footer';
    if (pinned(blocks[i].type) || pinned(blocks[target].type)) return blocks;

    const next = blocks.slice();
    const [moved] = next.splice(i, 1);
    next.splice(target, 0, moved);
    return next;
}
